import { useEffect, useRef } from 'react';
import { Box, Grid } from '@mui/material';
import FlightCard from './FlightCard';
import { IFlight } from '../../type';
import { OAEmpty, OAImage } from '../../components';

const ReturnsFlightList = ({
  flightOptions,
  selectedFlight,
  tripType,
  isInternational,
  onSelect,
  handleClearFilter,
  orgCode
}: {
  flightOptions: any;
  selectedFlight: any;
  tripType: 'R' | 'O';
  isInternational: boolean;
  onSelect: (obj: IFlight, type: 'onward' | 'return') => void;
  handleClearFilter: () => void;
  orgCode?: string;
}) => {
  const cardRefsOnward: any = useRef({});
  const cardRefsReturn: any = useRef({});
  const gridRefOnward = useRef(null);
  const gridRefReturn = useRef(null);

  // Reset styles of all cards
  const resetCardStyles = (cards: any) => {
    Object.values(cards).forEach((card: any) => {
      if (card) {
        // card.style.borderColor = '#DEE2E1';
        card.style.background = '#FDFDFD';
      }
    });
  };

  // Set style for the selected card
  const setCardStyle = (card: any) => {
    if (card) {
      card.style.background =  orgCode === 'ZOLVE' ? '#FFF5F2' : '#D9FCF3';
      // card.style.borderColor = '#00C6A8';
    }
  };

  // Delay the onSelect action
  const delaySelect = (flight: any, type: any) => {
    setTimeout(() => {
      onSelect(flight, type);
    }, 200);
  };

  const handleClick = (flight: any, type: any) => {
    const cardRefs = type === 'onward' ? cardRefsOnward.current : cardRefsReturn.current;
    resetCardStyles(cardRefs);
    setCardStyle(cardRefs[flight.id]);
    delaySelect(flight, type);
  };

  useEffect(() => {
    const { onwardFlightOptions = [], returnFlightOptions = [] } = flightOptions ?? {};

    // Default to the first flight ID if not found in the selectedFlight
    const onwardFlightId = onwardFlightOptions.some(
      (flight: any) => flight.id === selectedFlight?.onward?.id
    )
      ? selectedFlight.onward.id
      : onwardFlightOptions[0]?.id;

    const returnFlightId = returnFlightOptions.some(
      (flight: any) => flight.id === selectedFlight?.return?.id
    )
      ? selectedFlight.return.id
      : returnFlightOptions[0]?.id;

    // Function to set background color if the reference exists
    const highlightCard = (cardRefs: any, gridRef: any, flightId: any) => {
      if (flightId && cardRefs.current[flightId]) {
        cardRefs.current[flightId].style.background = orgCode === 'ZOLVE' ? '#FFF5F2' : '#D9FCF3';
        const scrollToPosition = cardRefs.current[flightId].offsetTop - 187;
        if (gridRef.current) {
          gridRef.current.scrollTop = scrollToPosition;
        }
      }
    };

    // Apply highlight if the options are available
    highlightCard(cardRefsOnward, gridRefOnward, onwardFlightId);
    highlightCard(cardRefsReturn, gridRefReturn, returnFlightId);
  }, [flightOptions]);

  return (
    <Grid container>
      <Grid
        item
        xs={6}
        ref={gridRefOnward}
        sx={{
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          height: orgCode === 'ZOLVE' ? 'calc(100vh - 222px)' : 'calc(100vh - 187px - 75px)',
        }}
      >
        {flightOptions?.onwardFlightOptions?.length > 0 ? (
          flightOptions?.onwardFlightOptions?.map((obj: IFlight, index: number) => (
            <Box
              ref={(el) => (cardRefsOnward.current[obj.id] = el)}
              key={obj.id}
              mb={flightOptions?.onwardFlightOptions?.length - 1 === index ? '90px' : 0}
              sx={{
                border: orgCode === 'ZOLVE' ? '0.5px solid #F6F6F6' :'0.5px solid #DEE2E1',
                bgcolor: '#FDFDFD',
              }}
            >
              <FlightCard
                data={obj}
                onClick={() => handleClick(obj, 'onward')}
                tripType={tripType}
                isInternational={isInternational}
                orgCode={orgCode}
              />
            </Box>
          ))
        ) : (
          <OAEmpty
            illustration={<OAImage src={orgCode === "ZOLVE" ? "no-results-found.svg" : "person-with-phone.svg"} folder={orgCode === 'ZOLVE' ? 'zolveErrorAssets' : 'niyoErrorAssets'} alt="empty" />}
            bodytext="No flights are available for this route"
            buttonText="Clear Filter"
            onClickBtn={handleClearFilter}
            customButtonHeight
            buttonWidth="110px"
          />
        )}
      </Grid>
      <Grid
        item
        xs={6}
        ref={gridRefReturn}
        sx={{
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          height: orgCode === 'ZOLVE' ? 'calc(100vh - 222px)' : 'calc(100vh - 187px - 75px)',
        }}
      >
        {flightOptions?.returnFlightOptions?.length > 0 ? (
          flightOptions?.returnFlightOptions?.map((obj: IFlight, index: number) => (
            <Box
              ref={(el) => (cardRefsReturn.current[obj.id] = el)}
              key={obj.id}
              mb={flightOptions?.returnFlightOptions?.length - 1 === index ? '90x' : 0}
              sx={{
                border: '0.5px solid #DEE2E1',
                bgcolor: '#FDFDFD',
              }}
            >
              <FlightCard
                data={obj}
                onClick={() => handleClick(obj, 'return')}
                tripType={tripType}
                isInternational={isInternational}
                orgCode={orgCode}
              />
            </Box>
          ))
        ) : (
          <OAEmpty
            illustration={<OAImage src={orgCode === "ZOLVE" ? "no-results-found.svg" : "person-with-phone.svg"} folder={orgCode === 'ZOLVE' ? 'zolveErrorAssets' : 'niyoErrorAssets'} alt="empty" />}
            bodytext="No flights are available for this route"
            buttonText="Clear Filter"
            onClickBtn={handleClearFilter}
            customButtonHeight
            buttonWidth="110px"
          />
        )}
      </Grid>
    </Grid>
  );
};

export default ReturnsFlightList;
